/**
 * Cart token export — outbound side of the Rodeo Soul Harmony LP cart handoff.
 *
 * When the user clicks a link tagged with `data-go-harmony` (or
 * `data-go-lp="<absolute URL to the LP>"`), this script:
 *   1. Reads the Liquid cart (GET /cart.js).
 *   2. Creates a Storefront API cart with the same line items.
 *   3. Redirects to the LP with `?cart=<token>` appended.
 *
 * Defensive:
 *   - No matching link clicked → script does nothing.
 *   - Empty Liquid cart → redirects to the LP URL without ?cart=.
 *   - Any error during cartCreate → still redirects (without ?cart=), so
 *     the user is never blocked.
 */
(function () {
  'use strict';

  var STOREFRONT_TOKEN = '1c6f1946a756cb3fde03372ff3bb3277';
  var STOREFRONT_API_URL = '/api/2025-01/graphql.json';
  var DEFAULT_LP_URL = 'https://harmony.truwestern.com/products/rodeo-soul-harmony-perfume';
  var SELECTOR = '[data-go-harmony], [data-go-lp], a[href^="https://harmony.truwestern.com"], a[href^="http://harmony.truwestern.com"], a[href$="/products/rodeo-soul-harmony-perfume"]';

  async function readLiquidCart() {
    var res = await fetch('/cart.js', {credentials: 'same-origin'});
    if (!res.ok) throw new Error('cart.js responded ' + res.status);
    return res.json();
  }

  async function createStorefrontCart(lines) {
    var body = {
      query:
        'mutation CartCreate($input: CartInput!) @inContext(country: US) {' +
        '  cartCreate(input: $input) {' +
        '    cart { id }' +
        '    userErrors { field message }' +
        '  }' +
        '}',
      variables: {input: {lines: lines}},
    };
    var res = await fetch(STOREFRONT_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': STOREFRONT_TOKEN,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Storefront API responded ' + res.status);
    var json = await res.json();
    var data = json && json.data && json.data.cartCreate;
    if (!data || !data.cart) throw new Error('cartCreate returned no cart');
    if (data.userErrors && data.userErrors.length) {
      throw new Error('cartCreate userErrors: ' + JSON.stringify(data.userErrors));
    }
    return data.cart.id;
  }

  function tokenFromCartGid(gid) {
    var m = String(gid || '').match(/\/Cart\/(.+)$/);
    return m ? m[1] : gid;
  }

  function appendCartTokenToUrl(url, token) {
    try {
      var parsed = new URL(url);
      if (token) parsed.searchParams.set('cart', token);
      return parsed.toString();
    } catch (e) {
      return url;
    }
  }

  async function goToLp(lpUrl) {
    var redirect = lpUrl;
    try {
      var cart = await readLiquidCart();
      var liquidLines = (cart && cart.items) || [];
      var sfLines = liquidLines
        .map(function (item) {
          // Liquid cart item: { variant_id, quantity, properties, ... }
          if (!item || !item.variant_id || !item.quantity) return null;
          var line = {
            merchandiseId: 'gid://shopify/ProductVariant/' + item.variant_id,
            quantity: item.quantity,
          };
          // Preserve line properties (e.g. _free_gift, gift wrap notes) as
          // Storefront cart attributes so round-trips keep GWP markers intact.
          if (item.properties && typeof item.properties === 'object') {
            var attrs = [];
            for (var k in item.properties) {
              if (Object.prototype.hasOwnProperty.call(item.properties, k)) {
                attrs.push({ key: k, value: String(item.properties[k]) });
              }
            }
            if (attrs.length) line.attributes = attrs;
          }
          return line;
        })
        .filter(Boolean);

      if (sfLines.length) {
        var cartGid = await createStorefrontCart(sfLines);
        var token = tokenFromCartGid(cartGid);
        redirect = appendCartTokenToUrl(lpUrl, token);
      }
    } catch (err) {
      if (window.console && console.debug) {
        console.debug('[cart-export] silent fail, redirecting without cart:', err && err.message);
      }
      // fall through to redirect without ?cart=
    }
    window.location.href = redirect;
  }

  function attach(el) {
    if (!el || el.__goHarmonyBound) return;
    el.__goHarmonyBound = true;
    el.addEventListener('click', function (e) {
      e.preventDefault();
      var lpUrl =
        el.getAttribute('data-go-lp') ||
        el.getAttribute('href') ||
        DEFAULT_LP_URL;
      // Optional UI feedback: disable + label so the user knows it's working.
      try {
        el.setAttribute('aria-busy', 'true');
        var prevText = el.textContent;
        el.textContent = 'Preparing your cart…';
        // Restore in case redirect takes longer than expected
        setTimeout(function () {
          el.removeAttribute('aria-busy');
          if (el.textContent === 'Preparing your cart…') {
            el.textContent = prevText;
          }
        }, 6000);
      } catch (e) { /* silent */ }
      goToLp(lpUrl);
    });
  }

  function attachAll() {
    document.querySelectorAll(SELECTOR).forEach(attach);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachAll);
  } else {
    attachAll();
  }

  // Watch for late-added elements (most themes mutate the cart drawer
  // after first paint).
  if (typeof MutationObserver !== 'undefined') {
    new MutationObserver(attachAll).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();
